// src/context/brandVoice/saveBrandIdentity.js — ONE "Salva modifiche", up to THREE independent writes.
//
// Decision 7 (2026-09-27), extended by v190: the document first, then the Italian voice, then
// the English voice. Each part succeeds or fails on its own (a doc 412/503 does not stop the
// voice) — EXCEPT:
//   - a doc 409 RUN_ACTIVE: an AI update rewrites the voice too, so BOTH voice halves are
//     SKIPPED ('runActive');
//   - the English on a workspace that owned no Italian file (`itOwned: false`) when the Italian
//     did not save: the backend would answer 409 CONTEXT_BASE_REQUIRED, so it is SKIPPED
//     ('italianFirst').
// ⚠️ The writes are separate routes with separate etags and no transaction between them, so a
// partly-saved outcome is a REAL state the screen must word per part — never "salvato" or
// "non salvato" for the whole form.
// The API is injected so this stays a plain async function (tested without React).
// PartResult = { status: 'none'|'saved'|'failed'|'skipped', response?, error?, reason? }
export async function saveBrandIdentity({
  api, tenant, doc, voice, voiceEn, itOwned = true, translate,
}) {
  const result = { doc: { status: 'none' }, voice: { status: 'none' }, voiceEn: { status: 'none' } };
  const docHas = doc && (Object.keys(doc.fields || {}).length || Object.keys(doc.fieldsEn || {}).length);
  if (docHas) {
    try {
      const response = await api.putBrandDocument({
        tenant, etag: doc.etag, fields: doc.fields, fieldsEn: doc.fieldsEn, translate,
      });
      result.doc = { status: 'saved', response };
    } catch (error) {
      result.doc = { status: 'failed', error };
    }
  }
  const runActive = result.doc.status === 'failed' && result.doc.error?.code === 'RUN_ACTIVE';
  if (voice) {
    if (runActive) {
      result.voice = { status: 'skipped', reason: 'runActive' };
    } else {
      try {
        // The Italian voice: deliberately NO `language` key (the route's default language).
        const response = await api.putBrandVoice({ tenant, etag: voice.etag, label: voice.label, axes: voice.axes });
        result.voice = { status: 'saved', response };
      } catch (error) {
        result.voice = { status: 'failed', error };
      }
    }
  }
  if (voiceEn) {
    if (runActive) {
      result.voiceEn = { status: 'skipped', reason: 'runActive' };
    } else if (!itOwned && result.voice.status !== 'saved') {
      result.voiceEn = { status: 'skipped', reason: 'italianFirst' };
    } else {
      try {
        const response = await api.putBrandVoice({
          tenant, language: 'en', etag: voiceEn.etag, label: voiceEn.label, axes: voiceEn.axes,
        });
        result.voiceEn = { status: 'saved', response };
      } catch (error) {
        result.voiceEn = { status: 'failed', error };
      }
    }
  }
  return result;
}

/** Did any saved field actually spend on a translation? (refresh the credits meter) */
export const spentOnTranslation = (docResponse) => (docResponse?.outcomes || [])
  .some((o) => o.translation === 'applied' && o.translation_reason !== 'CLEARED');

/**
 * Paths the server wrote but did NOT record as the tenant's (decision: warn + "Risalva").
 * ⭐ v186/v196: `fields` (optional) lets it skip every field a run never writes — the three "avoid" lists and
 * `verified_claims` — because "a future AI update could overwrite it" would be false there.
 */
export const unstampedPaths = (docResponse, fields) => {
  const neverAI = new Set((fields || [])
    .filter((f) => f?.policy === 'bucket_d' || f?.widget === 'claims').map((f) => f.field_path));
  return (docResponse?.outcomes || [])
    .filter((o) => o.written && o.stamped === false && !neverAI.has(o.field_path))
    .map((o) => o.field_path);
};

/** field_path → our per-field 422 marker, from details.errors[] (the message is NEVER used). */
export const invalidPathsFrom = (error) => new Set(
  ((error?.details && error.details.errors) || []).map((e) => e?.field_path).filter(Boolean),
);
